// paywallPlans.ts — what the paywall's plan cards display, kept pure so it can be unit-tested.
import type { MembershipPackages } from '@/services/purchases';


export interface PlanView {
  priceString: string;
  periodLabel: string;
  /** e.g. "£15.00"; annual card only, when the monthly equivalent is known. */
  perMonthString: string | null;
}
export interface PlansView {
  fallback: boolean;
  annual: PlanView | null;
  monthly: PlanView | null;
  /** Whole-number percent the annual plan saves against twelve monthly payments. */
  savingPercent: number | null;
}

function formatMoney(amount: number, currencyCode: string): string {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currencyCode }).format(amount);
  } catch {
    return amount.toFixed(2);
  }
}

export function savingPercent(annualPrice: number, monthlyPrice: number): number | null {
  if (!(annualPrice > 0) || !(monthlyPrice > 0)) return null;
  const pct = Math.round((1 - annualPrice / 12 / monthlyPrice) * 100);
  return pct > 0 ? pct : null;
}

export function buildPlans(pkgs: MembershipPackages | null): PlansView {
  const a = pkgs?.annual ?? null;
  const m = pkgs?.monthly ?? null;
  if (!a && !m) {
    // Store products didn't load: show the plan names with NO prices (never a price the store
    // didn't return; App Review). Subscribe stays disabled and the "very soon" note shows.
    return {
      fallback: true,
      annual: { priceString: '', periodLabel: '', perMonthString: null },
      monthly: { priceString: '', periodLabel: '', perMonthString: null },
      savingPercent: null,
    };
  }
  return {
    fallback: false,
    annual: a && {
      priceString: a.priceString,
      periodLabel: a.periodLabel,
      perMonthString: a.pricePerMonthString ?? (a.price > 0 ? formatMoney(a.price / 12, a.currencyCode) : null),
    },
    monthly: m && { priceString: m.priceString, periodLabel: m.periodLabel, perMonthString: null },
    savingPercent: a && m ? savingPercent(a.price, m.price) : null,
  };
}
